import { useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from "react";
import { ArrowUp, ImagePlus, Monitor, Square } from "lucide-react";
import {
  HELP_IMAGE_MIME_TYPES,
  HELP_IMAGES_MAX,
  HELP_QUESTION_MAX,
} from "@ttc-spectree/toolbox-contract";
import type { HelpAttachment } from "@/hooks/use-help-bridge";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { HelpThumbnail } from "./help-message";

/**
 * The Help ask box (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1557, frames
 * H1, H2 and H5, design `.ask`): a question, up to `HELP_IMAGES_MAX`
 * screenshots (pasted, dropped, picked, or "Attach this screen", which the
 * app captures from its own window), thumbnails with remove, and Send, which
 * becomes Stop while Help answers. Images go to the app over the bridge,
 * never to the SpecTree API. Disabled with the reason when Help cannot
 * answer (H5).
 *
 * Product Q&A (epic PI-SPEC-SW-E310, feature PI-SPEC-SW-1628) reuses the box
 * with its own words: `placeholder`, `ariaLabel` and `maxLength` default to
 * Help's, and without `onCaptureScreen` there is no "Attach this screen"
 * (it captures the Toolbox window, not the product).
 */

/** The image files in a paste or drop. */
function imageFiles(items: DataTransferItemList | null, files: FileList | null): File[] {
  const found: File[] = [];
  if (items) {
    for (const item of Array.from(items)) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (file) found.push(file);
    }
  }
  if (found.length === 0 && files) found.push(...Array.from(files));
  return found;
}

interface HelpAskBoxProps {
  draft: string;
  onDraftChange: (value: string) => void;
  attachments: readonly HelpAttachment[];
  attachError: string | null;
  asking: boolean;
  /** The reason Help cannot answer now; disables the box and replaces its placeholder (H5). */
  disabledReason?: string | undefined;
  onSend: () => void;
  onStop: () => void;
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  /** "Attach this screen"; without it the button is not shown. */
  onCaptureScreen?: (() => void) | undefined;
  /** The question box's placeholder. Help's by default. */
  placeholder?: string;
  /** The question box's accessible name. "Ask Help" by default. */
  ariaLabel?: string;
  /** The longest question. `HELP_QUESTION_MAX` by default. */
  maxLength?: number;
}

export function HelpAskBox({
  draft,
  onDraftChange,
  attachments,
  attachError,
  asking,
  disabledReason,
  onSend,
  onStop,
  onAddFiles,
  onRemove,
  onCaptureScreen,
  placeholder = "Ask about the Toolbox, or paste a screenshot…",
  ariaLabel = "Ask Help",
  maxLength = HELP_QUESTION_MAX,
}: HelpAskBoxProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const disabled = disabledReason !== undefined;
  const canSend = !disabled && !asking && draft.trim().length > 0;
  const full = attachments.length >= HELP_IMAGES_MAX;

  if (disabled) {
    return (
      <div
        className="flex min-h-16 flex-col gap-2 rounded-[10px] border border-dashed border-[#27272A] bg-[#0B0B0D] pb-2 pl-3 pr-2.5 pt-2.5"
        data-testid="help-ask-box"
        data-disabled="true"
      >
        <span
          className={cn("text-[13px]", TOOLBOX_TEXT.faint)}
          data-testid="help-ask-disabled-reason"
        >
          {disabledReason}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="flex-1" />
          <button
            type="button"
            disabled
            aria-label="Send"
            className={cn(
              "flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-[#27272A]",
              TOOLBOX_TEXT.faint
            )}
          >
            <ArrowUp className="h-[15px] w-[15px]" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = imageFiles(event.clipboardData.items, event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    onAddFiles(files);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files = imageFiles(event.dataTransfer.items, event.dataTransfer.files);
    if (files.length > 0) onAddFiles(files);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          "flex min-h-16 flex-col gap-2 rounded-[10px] border border-[#27272A] bg-[#121215] pb-2 pl-3 pr-2.5 pt-2.5",
          "focus-within:border-[#3F3F46] focus-within:shadow-[0_0_0_3px_rgba(35,164,255,0.18)]",
          dragging && "border-[#23A4FF]"
        )}
        onDragOver={(event) => {
          if (Array.from(event.dataTransfer.types).includes("Files")) {
            event.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
        data-testid="help-ask-box"
        data-disabled="false"
      >
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="help-ask-attachments">
            {attachments.map((attachment) => (
              <HelpThumbnail
                key={attachment.id}
                attachment={attachment}
                size="small"
                onRemove={() => {
                  onRemove(attachment.id);
                }}
              />
            ))}
          </div>
        )}
        <textarea
          value={draft}
          onChange={(event) => {
            onDraftChange(event.target.value);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          rows={2}
          maxLength={maxLength}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className="max-h-40 min-h-[20px] w-full resize-none bg-transparent text-[13px] leading-[1.5] text-[#FAFAFA] outline-none placeholder:text-[#8B8B94]"
          data-testid="help-ask-input"
        />
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={full}
            aria-label="Add an image"
            title="Add an image"
            className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg text-muted-foreground hover:bg-white/[0.04] hover:text-[#FAFAFA] disabled:opacity-50"
          >
            <ImagePlus className="h-[15px] w-[15px]" aria-hidden="true" />
          </button>
          <input
            ref={fileInput}
            type="file"
            accept={HELP_IMAGE_MIME_TYPES.join(",")}
            multiple
            className="hidden"
            data-testid="help-file-input"
            onChange={(event) => {
              const files = event.target.files ? Array.from(event.target.files) : [];
              event.target.value = "";
              if (files.length > 0) onAddFiles(files);
            }}
          />
          {onCaptureScreen && (
            <button
              type="button"
              onClick={onCaptureScreen}
              disabled={full}
              className="inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-transparent px-[9px] text-xs font-medium text-[#FAFAFA] hover:bg-white/[0.04] disabled:opacity-50"
            >
              <Monitor className="h-[13px] w-[13px]" aria-hidden="true" />
              Attach this screen
            </button>
          )}
          <span className="flex-1" />
          {asking ? (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-[#3F3F46] px-2.5 text-xs font-medium text-[#FAFAFA] hover:bg-white/[0.04]"
              data-testid="help-stop"
            >
              <Square className="h-[11px] w-[11px]" aria-hidden="true" />
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              aria-label="Send"
              className={cn(
                "flex h-7 w-7 flex-none items-center justify-center rounded-lg",
                canSend
                  ? "bg-[#E11837] text-white hover:bg-[#C8102E]"
                  : cn("bg-[#27272A]", TOOLBOX_TEXT.faint)
              )}
              data-testid="help-send"
            >
              <ArrowUp className="h-[15px] w-[15px]" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {attachError && (
        <span
          role="alert"
          className="px-0.5 text-xs text-[#FBBF24]"
          data-testid="help-attach-error"
        >
          {attachError}
        </span>
      )}
    </div>
  );
}
